import React, { useCallback, useEffect, useState } from 'react';

import type { EstimateSnapshot } from './types';
import { useAiAssistant } from './AiAssistantContext';
import { useChat } from './hooks/useChat';
import { ChatPanel } from './components/ChatPanel';
import { HistoryPanel } from './components/HistoryPanel';
import {
  BetaBadge,
  CloseButton,
  Drawer,
  ErrorBanner,
  Header,
  HeaderRight,
  NewChatButton,
  ResizeHandle,
  SessionId,
  Title,
} from './styles';

interface Props {
  open: boolean;
  onClose: () => void;
  getEstimateSnapshot: () => EstimateSnapshot;
}

export function AiAssistantPanel({ open, onClose, getEstimateSnapshot }: Props) {
  const {
    messages,
    sendMessage,
    resolveAction,
    cancelTurn,
    startNewSession,
    openSession,
    isStreaming,
    isHydrating,
    hydratedCount,
    error,
    sessionId,
    warmSession,
  } = useChat(getEstimateSnapshot);
  const { panelWidth: width, isResizing, startResize } = useAiAssistant();
  const [showHistory, setShowHistory] = useState(false);

  // Don't leave a stream running against a closed drawer; the input would still be
  // disabled when it reopens.
  useEffect(() => {
    if (!open) {
      cancelTurn();
    }
  }, [open, cancelTurn]);

  // Return to the chat only if the conversation actually loaded; on failure the list
  // stays up with the error banner, rather than dropping the user into a blank panel.
  const handleSelectConversation = useCallback(
    async (id: string) => {
      if (await openSession(id)) {
        setShowHistory(false);
      }
    },
    [openSession]
  );

  const handleNewChat = useCallback(() => {
    startNewSession();
    setShowHistory(false);
  }, [startNewSession]);

  return (
    <Drawer
      $open={open}
      $resizing={isResizing}
      $width={width}
      aria-hidden={!open}
      role="complementary"
    >
      <ResizeHandle
        aria-label="Resize AskCommit panel"
        aria-orientation="vertical"
        onMouseDown={startResize}
        role="separator"
      />
      <Header>
        <Title>
          {showHistory ? (
            'Your Chats'
          ) : (
            <>
              AskCommit <BetaBadge>Beta</BetaBadge>
            </>
          )}
        </Title>
        <HeaderRight>
          {!showHistory && sessionId && <SessionId>Session: {sessionId.slice(0, 8)}</SessionId>}
          <NewChatButton
            aria-label={showHistory ? 'Back to the current chat' : 'View past chats'}
            disabled={isStreaming}
            onClick={() => setShowHistory((previous) => !previous)}
            type="button"
          >
            {showHistory ? 'Back' : 'Chats'}
          </NewChatButton>
          {messages.length > 0 && (
            <NewChatButton
              aria-label="Start a new chat"
              disabled={isStreaming}
              onClick={handleNewChat}
              type="button"
            >
              New Chat
            </NewChatButton>
          )}
          <CloseButton aria-label="Close AskCommit" onClick={onClose} type="button">
            ×
          </CloseButton>
        </HeaderRight>
      </Header>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {showHistory ? (
        <HistoryPanel activeSessionId={sessionId} onSelect={handleSelectConversation} />
      ) : (
        <ChatPanel
          hydratedCount={hydratedCount}
          isHydrating={isHydrating}
          isStreaming={isStreaming}
          messages={messages}
          onComposerFocus={warmSession}
          onResolveAction={resolveAction}
          onSend={sendMessage}
          sessionId={sessionId}
        />
      )}
    </Drawer>
  );
}
